import { Box, Paper, Typography, Stack, Chip } from "@mui/material";
import {
    Hotel,
    DirectionsCar,
    LocalActivity,
    AccessTime,
    LocationOn,
    Schedule,
    Flight,
    Star as StarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ProgramProductItem = ({ product, index }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    // const theme = useTheme();

    const getIcon = () => {
        if (product.category_id === 3) return <Hotel fontSize="medium" sx={{ fontSize: 20 }} />;
        if (product.category_id === 2) return <DirectionsCar fontSize="medium" sx={{ fontSize: 20 }} />;
        if (product.product_type === 'flight') return <Flight fontSize="medium" sx={{ fontSize: 20 }} />;
        return <LocalActivity fontSize="medium" sx={{ fontSize: 20 }} />;
    };

    const getColor = () => {
        if (product.category_id === 3) return "info";
        if (product.category_id === 2) return "warning";
        return "success";
    };
    const renderFlightRoute = () => {
        const depCity =
            product.flight_details?.departure_airport?.city?.name?.[lang];

        const arrCity =
            product.flight_details?.arrival_airport?.city?.name?.[lang];

        if (!depCity || !arrCity) return null;

        return `${depCity} → ${arrCity}`;
    };


    const isHotel = product.category_id === 3 || product.product_type === 'hotel';
    const isFlight = product.product_type === 'flight';

    return (
        <Paper
            variant="outlined"
            sx={{
                p: isHotel ? 1.5 : 2,
                display: "flex",
                gap: 2,
                "@media print": {
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                },
                "&:hover": {
                    bgcolor: "action.hover",
                },
                alignItems: isHotel ? "stretch" : "center",
            }}
        >
            {/* Index - hidden for hotels */}
            {!isHotel && (
                <Box
                    sx={{
                        minWidth: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: `${getColor()}.main`,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: "1rem",
                    }}
                >
                    {index}
                </Box>
            )}

            {/* Details */}
            <Box sx={{ flex: 1 }}>
                {isHotel ? (
                    /* === Hotel: simplified card === */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Hotel sx={{ fontSize: 24, color: 'info.main' }} />
                        {product.hotel_details?.rating && (
                            <Chip
                                icon={<StarIcon sx={{ fontSize: 16 }} />}
                                label={`${product.hotel_details.rating.stars || product.hotel_details.rating.name?.[lang] || ''}`}
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={{ fontSize: '13px', fontWeight: 700, height: 26 }}
                            />
                        )}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '15px', lineHeight: 1.3 }}>
                                {product.hotel_details?.hotel_name || ''}
                            </Typography>
                        </Box>
                        <Chip
                            label={lang === 'ar' ? 'إقامة' : 'Accommodation'}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: '11px', fontWeight: 600, height: 24 }}
                        />
                    </Box>
                ) : (
                    /* === Non-hotel: full card === */
                    <>
                        <Stack direction="row" justifyContent={'space-between'} spacing={1} flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={1} sx={{ flex: 1 }} alignItems="center" mb={0.5}>
                                {getIcon()}
                                <Typography variant="h6" fontWeight="600" sx={{ fontSize: '16px' }}>
                                    {product.title?.[lang] || ''}
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: "flex-end" }} flexWrap="wrap" useFlexGap>
                                {product.time && (
                                    <Chip
                                        icon={<AccessTime sx={{ fontSize: 16 }} />}
                                        label={product.time}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {product.pickup_time && (
                                    <Chip
                                        icon={<Schedule sx={{ fontSize: 16 }} />}
                                        label={product.pickup_time}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {isFlight && (
                                    <>
                                        <Chip
                                            icon={<Flight sx={{ fontSize: 16 }} />}
                                            label={renderFlightRoute()}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                bgcolor: "rgba(25, 118, 210, 0.85)",
                                                color: "#fff",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                "& .MuiChip-icon": {
                                                    color: "#fff",
                                                },
                                            }}
                                        />
                                        {product.flight_details?.departure_date && (
                                            <Chip
                                                label={`${lang === 'ar' ? 'مغادرة' : 'Dep'}: ${product.flight_details.departure_date}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500 }}
                                            />
                                        )}
                                        {product.flight_details?.arrival_date && (
                                            <Chip
                                                label={`${lang === 'ar' ? 'وصول' : 'Arr'}: ${product.flight_details.arrival_date}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500 }}
                                            />
                                        )}
                                        {product.flight_details?.departure_time && (
                                            <Chip
                                                label={`${product.flight_details.departure_time} → ${product.flight_details.arrival_time || ''}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary' }}
                                            />
                                        )}
                                    </>
                                )}
                                {product.city && (
                                    <Chip
                                        icon={<LocationOn color="white" sx={{ color: "#fff", fontSize: 16 }} />}
                                        label={product.city?.name?.[lang] || product.city?.name?.en || product.city?.name?.ar || (typeof product.city === 'string' ? product.city : '')}
                                        size="small"
                                        variant="outlined"
                                        sx={{ bgcolor: "rgba(30, 136, 229, 0.8)", color: "#fff", fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                                {product.units && (
                                    <Chip
                                        label={`${product.units} ${lang === 'ar' ? 'وحدة' : 'Units'}`}
                                        size="small"
                                        color={getColor()}
                                        sx={{ fontSize: '12px', fontWeight: 500 }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default ProgramProductItem;
